import { BufferAttribute, BufferGeometry, Matrix4, Ray, Vector3 } from 'three';
import type { MeshBVH } from 'three-mesh-bvh';

/**
 * THE LIGHTMAPPER'S INNER LOOP.
 *
 * Split out from `bake-light.ts` for one reason: this half has to run inside a
 * Web Worker, and a worker cannot touch the scene graph. Everything here takes
 * plain geometry and plain numbers, so the same code runs on the main thread as
 * a fallback and in eight workers at once when the browser has them.
 *
 * The why of the whole thing — why the mall's forty-seven fixtures are baked
 * rather than rendered, and what baking buys beyond frame rate — is in
 * `bake-light.ts`. This file is just the arithmetic.
 */

/**
 * Longest edge, in metres, a triangle may have and still be baked as-is.
 *
 * This is the resolution of the whole lighting solution and it is the only knob
 * here worth much thought. Too coarse and a lamp on a wall lights the entire
 * wall evenly, because the wall has four corners and the falloff has nowhere to
 * happen. Too fine and load time and memory go up for detail nobody sees. At
 * 1.1m a two-metre pool of light under a ceiling panel has roughly four steps
 * across its radius, which flat shading and the fog then hide entirely.
 */
const MAX_EDGE = 1.1;

/**
 * Recursion depth cap. Presents itself as a safety valve; is actually the budget.
 *
 * Only the longest edge is split each pass, so a triangle needs roughly
 * 2·log2(its longest edge / MAX_EDGE) levels to get down to target — about
 * twelve for a floor slab. At five, no surface bigger than a few metres ever
 * reaches `MAX_EDGE` at all, which is worth knowing before reading the constant
 * above as a promise: it is the target, not the guarantee.
 *
 * Raising it to ten was measured, not guessed, and it is a cliff: 1.18M baked
 * vertices become 2.84M, the bake goes from 47 seconds to 128, and the drawn
 * triangle count roughly triples for lighting detail that flat shading and fog
 * were already hiding. Large surfaces stay coarse on purpose.
 *
 * Where a big surface genuinely needs the resolution, buy it in the authored
 * geometry instead — see `FLOOR_GRID` in `plan.ts`, which tessellates the one
 * class of surface the player spends the whole game looking at, and costs 130k
 * vertices rather than 1.7M.
 */
const MAX_SUBDIVISIONS = 5;

/**
 * How far a shadow ray starts from the surface, in metres.
 *
 * Without an offset every ray immediately hits the triangle it left, and the
 * entire mall bakes black. This is the classic shadow-acne bias and the value is
 * a compromise: too small and coplanar geometry self-hits anyway, too large and
 * light leaks under skirting boards and around door frames.
 */
const RAY_BIAS = 0.02;

/**
 * How much geometry immediately around a fixture is ignored when tracing to it.
 *
 * Nearly every light in this mall is authored INSIDE something — a batten inside
 * its housing, a case light under the case's top panel, a lamp head inside its
 * shade. Traced literally, all of those are sealed inside solid geometry, every
 * shadow ray hits their own housing, and the fixture bakes as switched off. This
 * is what a real lightmapper's "light is not an occluder of itself" rule buys,
 * done the cheap way: hits within this radius of the light do not count.
 *
 * It has to stay small. At half a metre it starts letting light through thin
 * partition walls that happen to run close behind a fitting.
 */
const LIGHT_CLEARANCE = 0.28;

/**
 * The light is treated as a small sphere of this radius, not a point.
 *
 * Hard vertex-sampled shadows are the worst-looking thing this bake can produce:
 * the shadow edge lands between two vertices and the interpolation turns it into
 * a hard diagonal stripe across a wall. Sampling a few points across a small
 * source gives a penumbra a few vertices wide, which reads as soft light and
 * hides the sampling entirely. Physically it is also just true — the fittings
 * are strips and panels, not points.
 */
const SOURCE_RADIUS = 0.32;

/**
 * Offsets across the light source, as a regular tetrahedron.
 *
 * Four fixed directions rather than random ones, because the bake has to be
 * deterministic: the screenshot harness compares frames byte for byte, and a
 * jittered bake would fail every comparison for no reason. Four is enough
 * because the penumbra it produces is then smoothed again by interpolation
 * across the triangle.
 */
const SOURCE_TAPS: ReadonlyArray<readonly [number, number, number]> = [
  [1, 1, 1],
  [1, -1, -1],
  [-1, 1, -1],
  [-1, -1, 1],
].map(([x, y, z]) => {
  const k = SOURCE_RADIUS / Math.sqrt(3);
  return [x * k, y * k, z * k] as const;
});

/**
 * A fixture, as the kernel sees it: position and radiance in world space, with
 * the falloff parameters three.js would have used.
 *
 * Flat number triples rather than `Vector3` so the whole light list survives a
 * `postMessage` structured clone without a rehydration step on the far side.
 */
export interface KernelLight {
  /** World position, `[x, y, z]`. */
  p: readonly [number, number, number];
  /** Colour premultiplied by intensity, `[r, g, b]`. */
  rad: readonly [number, number, number];
  /** Cutoff distance. Zero would mean infinite reach, which nothing here wants. */
  distance: number;
  decay: number;
}

/** One mesh's geometry, flattened for transfer. */
export interface KernelAttribute {
  array: Float32Array;
  itemSize: number;
}
export type KernelAttributes = Record<string, KernelAttribute>;

export interface KernelJob {
  attributes: KernelAttributes;
  /** Column-major 16, as `Matrix4.elements`. Identity for anything batched. */
  matrix: number[];
}

export interface KernelResult {
  /** The subdivided attribute set, plus `bakedLight`. */
  attributes: KernelAttributes;
  lit: number;
  rays: number;
}

/** Attribute name the shader patch in `materials.ts` reads. Kept in sync there. */
export const BAKED_LIGHT_ATTR = 'bakedLight';

/**
 * Split every triangle until no edge is longer than `MAX_EDGE`.
 *
 * Midpoint subdivision on the longest edge, repeated. It only ever adds
 * vertices along existing edges, so the silhouette is untouched — this changes
 * the sampling rate of the lighting, not the shape of the building. Cracks
 * between neighbouring triangles are possible in principle (one side splits, the
 * other does not) but harmless here: the split point sits exactly on the shared
 * edge, so the two surfaces still meet, and the only artefact is a shading
 * discontinuity along a seam that flat shading already has.
 *
 * All attributes are interleaved into one scratch pool and addressed by index,
 * so a split is a handful of number writes rather than a tree of small arrays.
 * The straightforward version of this — one JS array per vertex per attribute —
 * spent most of the bake in the allocator.
 *
 * Assumes non-indexed input, because splitting an indexed buffer means
 * rebuilding the index and deduplicating shared vertices.
 */
export function subdivide(
  attrs: KernelAttributes,
  reaches: (x: number, y: number, z: number, r: number) => boolean,
): KernelAttributes {
  const names = Object.keys(attrs);
  const pos = names.indexOf('position');
  if (pos < 0) return attrs;

  const arrays = names.map((n) => attrs[n].array);
  const sizes = names.map((n) => attrs[n].itemSize);
  const offsets: number[] = [];
  let stride = 0;
  for (const s of sizes) {
    offsets.push(stride);
    stride += s;
  }
  const px = offsets[pos];

  const srcCount = arrays[pos].length / sizes[pos];
  let pool = new Float32Array(srcCount * stride * 2);
  let poolCount = 0;

  const grow = () => {
    if ((poolCount + 1) * stride <= pool.length) return;
    const bigger = new Float32Array(pool.length * 2);
    bigger.set(pool);
    pool = bigger;
  };

  /** Copy source vertex `i` into the pool; returns its pool index. */
  const take = (i: number) => {
    grow();
    const o = poolCount * stride;
    for (let k = 0; k < arrays.length; k++) {
      const a = arrays[k];
      const s = sizes[k];
      for (let c = 0; c < s; c++) pool[o + offsets[k] + c] = a[i * s + c];
    }
    return poolCount++;
  };

  const midpoint = (a: number, b: number) => {
    grow();
    const o = poolCount * stride;
    const oa = a * stride;
    const ob = b * stride;
    for (let c = 0; c < stride; c++) pool[o + c] = (pool[oa + c] + pool[ob + c]) / 2;
    return poolCount++;
  };

  const edge = (a: number, b: number) => {
    const oa = a * stride + px;
    const ob = b * stride + px;
    return Math.hypot(pool[oa] - pool[ob], pool[oa + 1] - pool[ob + 1], pool[oa + 2] - pool[ob + 2]);
  };

  const tris: number[] = [];
  const emit = (a: number, b: number, c: number, depth: number) => {
    const ab = edge(a, b);
    const bc = edge(b, c);
    const ca = edge(c, a);
    const longest = Math.max(ab, bc, ca);
    /**
     * Resolution is only worth buying where a light can reach.
     *
     * Static batching merges by 22-metre cell, so a batch's bounding sphere is
     * always big enough to touch something and a per-MESH reachability test
     * never rejects anything — it read as a no-op that had tripled the triangle
     * count of the entire building. The question has to be asked per triangle.
     */
    if (depth === 0 || longest > MAX_EDGE) {
      const oa = a * stride + px;
      const ob = b * stride + px;
      const oc = c * stride + px;
      const cx = (pool[oa] + pool[ob] + pool[oc]) / 3;
      const cy = (pool[oa + 1] + pool[ob + 1] + pool[oc + 1]) / 3;
      const cz = (pool[oa + 2] + pool[ob + 2] + pool[oc + 2]) / 3;
      if (!reaches(cx, cy, cz, longest)) {
        tris.push(a, b, c);
        return;
      }
    }
    if (longest <= MAX_EDGE || depth >= MAX_SUBDIVISIONS) {
      tris.push(a, b, c);
      return;
    }
    // Always split the longest edge. Splitting all three at once quadruples the
    // count every pass and produces far more vertices for the same resolution.
    if (ab === longest) {
      const m = midpoint(a, b);
      emit(a, m, c, depth + 1);
      emit(m, b, c, depth + 1);
    } else if (bc === longest) {
      const m = midpoint(b, c);
      emit(a, b, m, depth + 1);
      emit(a, m, c, depth + 1);
    } else {
      const m = midpoint(c, a);
      emit(a, b, m, depth + 1);
      emit(m, b, c, depth + 1);
    }
  };

  for (let i = 0; i < srcCount; i += 3) emit(take(i), take(i + 1), take(i + 2), 0);
  if (tris.length === srcCount) return attrs;

  const out: KernelAttributes = {};
  for (let k = 0; k < names.length; k++) {
    const s = sizes[k];
    const dst = new Float32Array(tris.length * s);
    for (let t = 0; t < tris.length; t++) {
      const o = tris[t] * stride + offsets[k];
      for (let c = 0; c < s; c++) dst[t * s + c] = pool[o + c];
    }
    out[names[k]] = { array: dst, itemSize: s };
  }
  return out;
}

/**
 * Three.js's own point-light falloff, reproduced exactly.
 *
 * Matching the renderer matters more than the formula being pretty. Every
 * intensity and reach in the world files was tuned by looking at what the
 * realtime lights did, and a bake that used a different curve would quietly
 * rescale the entire building's lighting — every one of those numbers would then
 * have to be re-judged by eye against a moving target.
 */
function attenuation(d: number, cutoff: number, decay: number): number {
  const falloff = 1 / Math.max(Math.pow(d, decay), 0.01);
  if (cutoff <= 0) return falloff;
  const t = Math.max(0, Math.min(1, 1 - Math.pow(d / cutoff, 4)));
  return falloff * t * t;
}

/** Lambert's `1/PI`, so a baked surface matches what the light loop produced. */
const RECIPROCAL_PI = 1 / Math.PI;

/**
 * Normals are needed and the input may not carry them.
 *
 * Flat per-face normals, which is correct for this building: every surface in it
 * is flat-shaded by design, and computing smooth normals here would disagree
 * with what the renderer draws.
 */
function faceNormals(position: Float32Array): Float32Array {
  const out = new Float32Array(position.length);
  for (let i = 0; i < position.length; i += 9) {
    const ax = position[i], ay = position[i + 1], az = position[i + 2];
    const bx = position[i + 3], by = position[i + 4], bz = position[i + 5];
    const cx = position[i + 6], cy = position[i + 7], cz = position[i + 8];
    const e1x = bx - ax, e1y = by - ay, e1z = bz - az;
    const e2x = cx - ax, e2y = cy - ay, e2z = cz - az;
    let nx = e1y * e2z - e1z * e2y;
    let ny = e1z * e2x - e1x * e2z;
    let nz = e1x * e2y - e1y * e2x;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len; ny /= len; nz /= len;
    for (let k = 0; k < 9; k += 3) {
      out[i + k] = nx;
      out[i + k + 1] = ny;
      out[i + k + 2] = nz;
    }
  }
  return out;
}

const _matrix = /* @__PURE__ */ new Matrix4();
const _inverse = /* @__PURE__ */ new Matrix4();
const _ray = /* @__PURE__ */ new Ray();
const _world = /* @__PURE__ */ new Vector3();
const _normal = /* @__PURE__ */ new Vector3();
const _probe = /* @__PURE__ */ new Vector3();
const _tap = /* @__PURE__ */ new Vector3();

/**
 * Subdivide one mesh's geometry and bake every light that reaches it.
 *
 * `bvh` is the whole static world as an occluder set, in world space. Pass null
 * to skip shadowing entirely, which is only useful for diagnosis — unoccluded
 * baked light looks exactly like the realtime lights this replaced.
 */
/**
 * The half of the bake that is arithmetic: subdivide, and nothing else.
 *
 * Split out because it is the only half worth doing at load time. It is
 * deterministic — same attributes, same lights, same vertices in the same order
 * on every machine — which is what lets `bake-cache.ts` ship the raycast half as
 * a file of irradiance and have the two line up. `bakedLight` comes back zeroed
 * for the caller to fill.
 */
export function tessellateJob(job: KernelJob, lights: KernelLight[]): KernelResult {
  const dense = tessellate(job, lights);
  const n = dense.position.array.length / 3;
  dense[BAKED_LIGHT_ATTR] = { array: new Float32Array(n * 3), itemSize: 3 };
  return { attributes: dense, lit: 0, rays: 0 };
}

/** Shared prologue: light set into mesh space, then split to `MAX_EDGE`. */
function tessellate(job: KernelJob, lights: KernelLight[]): KernelAttributes {
  _matrix.fromArray(job.matrix);
  _inverse.copy(_matrix).invert();

  // Which fixtures reach this mesh at all. Asked in the mesh's own space so the
  // per-triangle test below is a plain distance compare with no transform in the
  // inner loop.
  const local: KernelLight[] = [];
  for (const l of lights) {
    _probe.set(l.p[0], l.p[1], l.p[2]).applyMatrix4(_inverse);
    local.push({ p: [_probe.x, _probe.y, _probe.z], rad: l.rad, distance: l.distance, decay: l.decay });
  }

  const dense = subdivide(job.attributes, (x, y, z, r) => {
    for (const l of local) {
      const dx = l.p[0] - x, dy = l.p[1] - y, dz = l.p[2] - z;
      if (Math.hypot(dx, dy, dz) < l.distance + r) return true;
    }
    return false;
  });
  if (!dense.normal) dense.normal = { array: faceNormals(dense.position.array), itemSize: 3 };
  return dense;
}

export function bakeJob(job: KernelJob, lights: KernelLight[], bvh: MeshBVH | null): KernelResult {
  const dense = tessellate(job, lights);
  // Re-set rather than relying on what `tessellate` left in the scratch, which
  // happens to be right and would stop being right the moment anything else
  // borrowed it.
  _matrix.fromArray(job.matrix);

  const position = dense.position.array;
  const n = position.length / 3;
  const normals = dense.normal!.array;

  const baked = new Float32Array(n * 3);
  let lit = 0;
  let rays = 0;

  for (let i = 0; i < n; i++) {
    _world.set(position[i * 3], position[i * 3 + 1], position[i * 3 + 2]).applyMatrix4(_matrix);
    _normal
      .set(normals[i * 3], normals[i * 3 + 1], normals[i * 3 + 2])
      .transformDirection(_matrix);
    let r = 0;
    let g = 0;
    let b = 0;

    for (const l of lights) {
      const tx = l.p[0] - _world.x;
      const ty = l.p[1] - _world.y;
      const tz = l.p[2] - _world.z;
      const d = Math.hypot(tx, ty, tz);
      if (d > l.distance || d < 1e-4) continue;
      const ndl = (_normal.x * tx + _normal.y * ty + _normal.z * tz) / d;
      // Back faces get nothing, and they get it before the expensive part —
      // roughly half of all vertex/light pairs in a closed building.
      if (ndl <= 0) continue;

      let visible = 1;
      if (bvh) {
        _ray.origin.copy(_world).addScaledVector(_normal, RAY_BIAS);
        let clear = 0;
        for (const tap of SOURCE_TAPS) {
          _tap.set(l.p[0] + tap[0], l.p[1] + tap[1], l.p[2] + tap[2]).sub(_ray.origin);
          const td = _tap.length();
          _ray.direction.copy(_tap).divideScalar(td);
          rays++;
          // DoubleSide, and the ray stops short of the fixture itself: a wall
          // built as a single plane has no back face, so culling by side here
          // would let light through half the building, and a hit inside the
          // fitting's own housing is not an occlusion. See `LIGHT_CLEARANCE`.
          if (!bvh.raycastFirst(_ray, 2, 0, Math.max(0, td - LIGHT_CLEARANCE))) clear++;
        }
        if (!clear) continue;
        visible = clear / SOURCE_TAPS.length;
      }

      const k = visible * ndl * attenuation(d, l.distance, l.decay) * RECIPROCAL_PI;
      r += l.rad[0] * k;
      g += l.rad[1] * k;
      b += l.rad[2] * k;
    }

    if (r + g + b > 0) lit++;
    baked[i * 3] = r;
    baked[i * 3 + 1] = g;
    baked[i * 3 + 2] = b;
  }

  dense[BAKED_LIGHT_ATTR] = { array: baked, itemSize: 3 };
  return { attributes: dense, lit, rays };
}

/** Flatten a geometry for the kernel. Non-indexed, because `subdivide` needs it. */
export function toKernelAttributes(geo: BufferGeometry): KernelAttributes {
  const src = geo.index ? geo.toNonIndexed() : geo;
  const out: KernelAttributes = {};
  for (const name of Object.keys(src.attributes)) {
    const a = src.getAttribute(name) as BufferAttribute;
    // Copied, never aliased: two meshes may share one geometry, and the copy is
    // also what makes the buffer safe to hand to a worker.
    out[name] = { array: new Float32Array(a.array as ArrayLike<number>), itemSize: a.itemSize };
  }
  if (src !== geo) src.dispose();
  return out;
}

/** Rebuild a geometry from what the kernel returned. */
export function fromKernelAttributes(attrs: KernelAttributes): BufferGeometry {
  const geo = new BufferGeometry();
  for (const name of Object.keys(attrs)) {
    geo.setAttribute(name, new BufferAttribute(attrs[name].array, attrs[name].itemSize));
  }
  return geo;
}

/** Every buffer in an attribute set, for a `postMessage` transfer list. */
export function transferables(attrs: KernelAttributes): ArrayBuffer[] {
  return Object.keys(attrs).map((n) => attrs[n].array.buffer as ArrayBuffer);
}
